import React, { useEffect, useLayoutEffect, useRef } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { TFunction } from 'i18next';
import {
  ChatAsset,
  ChatLibrarySourceType,
  ChatReactionEmoji,
  ChatTimelineItem,
} from '../../models/Chat';
import ChatAssetAttachment from './ChatAssetAttachment';
import ChatLinkAttachment from './ChatLinkAttachment';
import ChatMessageReaction, { ChatMessageReactionBadges } from './ChatMessageReaction';
import ChatSaveToLibraryButton from './ChatSaveToLibraryButton';
import ChatEmojiPicker from './ChatEmojiPicker';
import { CHAT_REACTION_DOUBLE_CLICK_EMOJI } from '../../utils/chatEmojis';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import { insertTextAtSelection } from '../../utils/insertTextAtSelection';
import {
  formatChatDayDivider,
  formatChatMessageTime,
  isChatLibrarySaved,
  shouldShowChatMessageHeader,
  chatAvatarColor,
  chatMessageDayKey,
  chatTimelineAssetIds,
  chatTimelineDisplayBody,
} from '../../utils/chat';
import { ChatAvatarMap, chatAvatarSrc } from '../../utils/chatAvatars';

interface ChatTimelinePanelProps {
  chatId: string;
  items: ChatTimelineItem[];
  assetsById: Map<string, ChatAsset>;
  avatarByUserId: ChatAvatarMap;
  librarySavedKeys: ReadonlySet<string>;
  librarySavingKey: string | null;
  partnerName: string;
  currentUserId?: string;
  timelineBody: string;
  postingTimeline: boolean;
  refreshingTimeline?: boolean;
  timelineError: string | null;
  locale: string;
  t: TFunction;
  onBodyChange: (value: string) => void;
  onPost: () => void;
  onAttachFile?: () => void;
  onSaveToLibrary?: (sourceType: ChatLibrarySourceType, sourceId: string) => void;
  onSetReaction?: (itemId: string, emoji: ChatReactionEmoji) => void;
  onClearReaction?: (itemId: string) => void;
  reactionBusyItemId?: string | null;
  onTimelineRendered?: () => void;
  /** Optional actions shown above the composer (e.g. support case Resolve/Close). */
  composerActions?: React.ReactNode;
}

function renderTimelineAttachments(
  item: ChatTimelineItem,
  assetsById: Map<string, ChatAsset>
) {
  const assetIds = chatTimelineAssetIds(item);
  if (!assetIds.length) return null;

  const attachments = assetIds
    .map((assetId) => assetsById.get(assetId))
    .filter((asset): asset is ChatAsset => Boolean(asset));

  if (!attachments.length) return null;

  const displayBody = chatTimelineDisplayBody(item, assetsById);

  return (
    <Stack spacing={0.75} sx={{ mt: displayBody ? 0.75 : 0 }}>
      {attachments.map((asset, index) => {
        const mt = index === 0 && !displayBody ? 0 : undefined;

        if (asset.kind === 'LINK') {
          return <ChatLinkAttachment key={asset.id} asset={asset} mt={mt} />;
        }

        return (
          <ChatAssetAttachment key={asset.id} asset={asset} variant="timeline" mt={mt} />
        );
      })}
    </Stack>
  );
}

function renderMessageBody(
  item: ChatTimelineItem,
  assetsById: Map<string, ChatAsset>,
  t: TFunction
) {
  const isNote = item.kind === 'NOTE';
  const isSystem = item.kind === 'SYSTEM';
  const displayBody = chatTimelineDisplayBody(item, assetsById);

  return (
    <Box
      sx={{
        ...(isNote && {
          borderLeft: 3,
          borderColor: 'warning.main',
          pl: 1.5,
          py: 0.25,
          bgcolor: 'rgba(255, 182, 72, 0.08)',
          borderRadius: '0 6px 6px 0',
        }),
        ...(isSystem && {
          fontStyle: 'italic',
          color: 'text.secondary',
        }),
      }}
    >
      {isNote && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
          <PushPinOutlinedIcon sx={{ fontSize: 14, color: 'warning.main' }} />
          <Typography variant="caption" color="warning.main" fontWeight={600}>
            {t('chat.timeline.kindNote', 'Note')}
          </Typography>
        </Stack>
      )}
      {displayBody && (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.45,
            fontSize: '0.9375rem',
          }}
        >
          {displayBody}
        </Typography>
      )}
      {renderTimelineAttachments(item, assetsById)}
    </Box>
  );
}

function scrollFeedToBottom(feed: HTMLDivElement) {
  feed.scrollTop = feed.scrollHeight;
}

function scheduleScrollFeedToBottom(feed: HTMLDivElement) {
  const run = () => scrollFeedToBottom(feed);

  run();
  const rafId = requestAnimationFrame(() => {
    requestAnimationFrame(run);
  });
  const timeoutIds = [0, 150].map((delay) => window.setTimeout(run, delay));

  return () => {
    cancelAnimationFrame(rafId);
    timeoutIds.forEach((id) => window.clearTimeout(id));
  };
}

const ChatTimelinePanel: React.FC<ChatTimelinePanelProps> = ({
  chatId,
  items,
  assetsById,
  avatarByUserId,
  librarySavedKeys,
  librarySavingKey,
  partnerName,
  currentUserId,
  timelineBody,
  postingTimeline,
  refreshingTimeline = false,
  timelineError,
  locale,
  t,
  onBodyChange,
  onPost,
  onAttachFile,
  onSaveToLibrary,
  onSetReaction,
  onClearReaction,
  reactionBusyItemId = null,
  onTimelineRendered,
  composerActions,
}) => {
  const feedRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingCursorRef = useRef<number | null>(null);
  const previousItemsLengthRef = useRef<number | null>(null);
  const refreshScrollAnchorRef = useRef<number | null>(null);
  const pendingScrollRestoreRef = useRef(false);

  useLayoutEffect(() => {
    previousItemsLengthRef.current = null;
  }, [chatId]);

  useLayoutEffect(() => {
    if (refreshingTimeline || pendingScrollRestoreRef.current) return;
    onTimelineRendered?.();
  }, [chatId, items, refreshingTimeline, onTimelineRendered]);

  useEffect(() => {
    const feed = feedRef.current;
    if (!feed) return;

    if (refreshingTimeline) {
      if (refreshScrollAnchorRef.current === null) {
        refreshScrollAnchorRef.current = feed.scrollHeight - feed.scrollTop;
        pendingScrollRestoreRef.current = true;
      }
      return;
    }

    if (refreshScrollAnchorRef.current !== null) {
      const anchor = refreshScrollAnchorRef.current;
      refreshScrollAnchorRef.current = null;

      let cancelled = false;
      const restoreScroll = () => {
        if (cancelled || !feedRef.current) return;
        feedRef.current.scrollTop = Math.max(0, feedRef.current.scrollHeight - anchor);
        pendingScrollRestoreRef.current = false;
      };

      const timeoutId = window.setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(restoreScroll);
        });
      }, 0);

      return () => {
        cancelled = true;
        window.clearTimeout(timeoutId);
      };
    }
  }, [refreshingTimeline, items]);

  useLayoutEffect(() => {
    const feed = feedRef.current;
    if (!feed || refreshingTimeline || pendingScrollRestoreRef.current) return;

    const previousLength = previousItemsLengthRef.current;
    const hasNewItems = previousLength !== null && items.length > previousLength;
    const isChatOpen = previousLength === null;
    previousItemsLengthRef.current = items.length;

    if (!isChatOpen && !hasNewItems && !postingTimeline) return;

    return scheduleScrollFeedToBottom(feed);
  }, [chatId, items.length, postingTimeline, refreshingTimeline]);

  useEffect(() => {
    const cursorPosition = pendingCursorRef.current;
    if (cursorPosition === null) return;

    pendingCursorRef.current = null;
    const composer = composerRef.current;
    if (!composer) return;

    composer.focus();
    composer.setSelectionRange(cursorPosition, cursorPosition);
  }, [timelineBody]);

  const handleComposerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!postingTimeline && timelineBody.trim()) {
        onPost();
      }
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    const composer = composerRef.current;
    const { nextValue, cursorPosition } = insertTextAtSelection(
      timelineBody,
      emoji,
      composer?.selectionStart ?? timelineBody.length,
      composer?.selectionEnd ?? timelineBody.length
    );

    pendingCursorRef.current = cursorPosition;
    onBodyChange(nextValue);
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        ref={feedRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 1.5, sm: 2.5 },
          py: 2,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(255,255,255,0.15)',
            borderRadius: 4,
          },
        }}
      >
        {items.length === 0 ? (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
              {t('chat.timeline.welcomeTitle', 'This is the start of your chat')}
            </Typography>
            <Typography variant="body2">
              {t(
                'chat.timeline.welcomeBody',
                'Say hello to {{partner}}. Messages, notes, and shared files will show up here.',
                { partner: partnerName }
              )}
            </Typography>
          </Box>
        ) : (
          items.map((item, index) => {
            const previous = index > 0 ? items[index - 1] : undefined;
            const showHeader = shouldShowChatMessageHeader(item, previous);
            const showDayDivider =
              index === 0 || chatMessageDayKey(item.createdAt) !== chatMessageDayKey(previous!.createdAt);
            const authorName = item.authorName || t('chat.timeline.unknownAuthor', 'Someone');
            const canSaveMessage =
              onSaveToLibrary &&
              (item.kind === 'MESSAGE' || item.kind === 'NOTE' || item.kind === 'FILE_SHARED');
            const canReact =
              Boolean(onSetReaction && onClearReaction) && item.kind === 'MESSAGE';
            const messageSaved = canSaveMessage
              ? isChatLibrarySaved(librarySavedKeys, 'MESSAGE', item.id)
              : false;
            const messageSaving = librarySavingKey === `MESSAGE:${item.id}`;
            const reactionBusy = reactionBusyItemId === item.id;
            const hasHoverActions = canReact || canSaveMessage;

            return (
              <React.Fragment key={item.id}>
                {showDayDivider && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ my: 2, opacity: 0.85 }}
                  >
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                    <Chip
                      size="small"
                      label={formatChatDayDivider(item.createdAt, locale, t)}
                      sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        border: 1,
                        borderColor: 'divider',
                      }}
                    />
                    <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                  </Stack>
                )}

                <Stack
                  direction="row"
                  spacing={1.5}
                  className="chat-timeline-message"
                  onDoubleClick={
                    canReact
                      ? (event) => {
                          event.preventDefault();
                          window.getSelection()?.removeAllRanges();
                          if (item.reactionEmoji === CHAT_REACTION_DOUBLE_CLICK_EMOJI) {
                            onClearReaction?.(item.id);
                          } else {
                            onSetReaction?.(item.id, CHAT_REACTION_DOUBLE_CLICK_EMOJI);
                          }
                        }
                      : undefined
                  }
                  sx={{
                    position: 'relative',
                    py: showHeader ? 0.75 : 0.15,
                    px: 0.5,
                    mx: -0.5,
                    borderRadius: 1,
                    userSelect: canReact ? 'text' : undefined,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                    '&:hover .chat-message-actions, &:focus-within .chat-message-actions, & .chat-message-actions:has([aria-expanded="true"])':
                      {
                        opacity: 1,
                        pointerEvents: 'auto',
                      },
                  }}
                >
                  {showHeader ? (
                    <Avatar
                      src={chatAvatarSrc(avatarByUserId, item.authorUserId)}
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        bgcolor: chatAvatarColor(item.authorUserId),
                        mt: 0.25,
                      }}
                    >
                      {tutorAvatarInitials(authorName || 'BS')}
                    </Avatar>
                  ) : (
                    <Box sx={{ width: 36, flexShrink: 0 }} />
                  )}

                  <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                    {hasHoverActions && (
                      <Stack
                        className="chat-message-actions"
                        direction="row"
                        spacing={0}
                        alignItems="center"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: 0,
                          zIndex: 2,
                          opacity: 0,
                          pointerEvents: 'none',
                          transition: 'opacity 0.12s ease',
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          px: 0.25,
                          py: 0.15,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                        }}
                      >
                        {canReact && (
                          <ChatMessageReaction
                            reactionEmoji={item.reactionEmoji ?? null}
                            busy={reactionBusy}
                            t={t}
                            onSelect={(emoji) => onSetReaction?.(item.id, emoji)}
                            onClear={() => onClearReaction?.(item.id)}
                          />
                        )}
                        {canSaveMessage && (
                          <ChatSaveToLibraryButton
                            saved={messageSaved}
                            saving={messageSaving}
                            onClick={() => onSaveToLibrary('MESSAGE', item.id)}
                          />
                        )}
                      </Stack>
                    )}
                    {showHeader && (
                      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.25 }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                          {authorName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatChatMessageTime(item.createdAt, locale)}
                        </Typography>
                        {item.kind === 'FILE_SHARED' && (
                          <Typography variant="caption" color="text.secondary">
                            · {t('chat.timeline.kindFile', 'shared a file')}
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {!showHeader && item.kind === 'FILE_SHARED' && (
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                        {formatChatMessageTime(item.createdAt, locale)} ·{' '}
                        {t('chat.timeline.kindFile', 'shared a file')}
                      </Typography>
                    )}
                    {renderMessageBody(item, assetsById, t)}
                    {canReact && (
                      <ChatMessageReactionBadges
                        reactions={item.reactions ?? []}
                        reactionEmoji={item.reactionEmoji ?? null}
                        currentUserId={currentUserId}
                        busy={reactionBusy}
                        t={t}
                        onSelect={(emoji) => onSetReaction?.(item.id, emoji)}
                        onClear={() => onClearReaction?.(item.id)}
                      />
                    )}
                  </Box>
                </Stack>
              </React.Fragment>
            );
          })
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          mb: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'rgba(0,0,0,0.2)',
        }}
      >
        <Stack spacing={1}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: composerActions ? 0.75 : 0,
              px: 1,
              py: 0.75,
              borderRadius: 2.5,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'rgba(255,255,255,0.04)',
              transition: 'border-color 0.15s ease',
              '&:focus-within': {
                borderColor: 'primary.main',
              },
            }}
          >
            {composerActions && (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="wrap"
                useFlexGap
                sx={{
                  pb: 0.75,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                {composerActions}
              </Stack>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {onAttachFile && (
                <IconButton
                  size="small"
                  onClick={onAttachFile}
                  disabled={postingTimeline}
                  aria-label={t('chat.files.upload', 'Upload file')}
                  sx={{ flexShrink: 0, alignSelf: 'flex-end', mb: 0.5, color: 'text.secondary' }}
                >
                  <AttachFileRoundedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}

              <ChatEmojiPicker
                disabled={postingTimeline}
                t={t}
                onSelect={handleEmojiSelect}
              />

              <TextField
                multiline
                minRows={1}
                maxRows={6}
                placeholder={t('chat.timeline.composerPlaceholder', 'Message {{partner}}', {
                  partner: partnerName,
                })}
                value={timelineBody}
                onChange={(e) => onBodyChange(e.target.value)}
                onKeyDown={handleComposerKeyDown}
                disabled={postingTimeline}
                fullWidth
                variant="standard"
                inputRef={composerRef}
                sx={{ flex: 1, minWidth: 0 }}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    py: 0.25,
                    fontSize: '0.9375rem',
                    alignItems: 'center',
                    '& textarea': {
                      padding: 0,
                      lineHeight: 1.45,
                      resize: 'none',
                      overflow: 'auto',
                    },
                  },
                }}
              />

              <IconButton
                size="small"
                color="primary"
                onClick={onPost}
                disabled={postingTimeline || !timelineBody.trim()}
                aria-label={t('chat.timeline.post', 'Post')}
                sx={{ flexShrink: 0, alignSelf: 'flex-end', mb: 0.5 }}
              >
                <SendRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>
            {t('chat.timeline.composerHint', 'Enter to send · Shift+Enter for a new line')}
          </Typography>

          {timelineError && <Alert severity="error">{timelineError}</Alert>}
        </Stack>
      </Box>
    </Box>
  );
};

export default ChatTimelinePanel;
